import React, { useState, useEffect, useRef } from 'react';
import { PageId, HoverInsight } from './types';
import Sidebar from './components/Sidebar';
import Chatbot from './components/Chatbot';
import LoginPage from './pages/LoginPage';
import Overview from './pages/Overview';
import Scorecard from './pages/Scorecard';
import DtcKpis from './pages/DtcKpis';
import ShopifyDTC from './pages/ShopifyDTC';
import ZohoB2B from './pages/ZohoB2B';
import MetaAds from './pages/MetaAds';
import GoogleAds from './pages/GoogleAds';
import HubSpot from './pages/HubSpot';
import DesignTracker from './pages/DesignTracker';
import ShopifyMerchandising from './pages/ShopifyMerchandising';
import CategoryOps from './pages/CategoryOps';
import Unicommerce from './pages/Unicommerce';
import Bitespeed from './pages/Bitespeed';
import ShopifyTags from './pages/ShopifyTags';
import { Calendar, ShieldCheck } from 'lucide-react';
import DrillDownDashboard from './components/DrillDownDashboard';
import AdminPortal from './components/AdminPortal';
import { ProfileMenu } from './components/ProfileMenu';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GlobalDateRangeProvider } from './context/GlobalDateRangeContext';

function DashboardShell() {
  const { user, loading, logout } = useAuth();
  const [activePage, setActivePage] = useState<PageId>('overview');
  const [drillDownId, setDrillDownId] = useState<string | null>(null);
  const [showAdminPortal, setShowAdminPortal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDrillDownId(null);
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [activePage]);

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [drillDownId]);

  // Retained no-op to satisfy existing page prop contracts (onHover)
  const handleHover = (_insight: HoverInsight | null) => {};

  const renderActivePage = () => {
    switch (activePage) {
      case 'overview':
        return <Overview onHover={handleHover} />;
      case 'scorecard':
        return <Scorecard onHover={handleHover} />;
      case 'dtc-kpis':
        return <DtcKpis onHover={handleHover} />;
      case 'shopify':
        return <ShopifyDTC onHover={handleHover} />;
      case 'zoho':
        return <ZohoB2B onHover={handleHover} />;
      case 'meta-ads':
        return <MetaAds onHover={handleHover} />;
      case 'google-ads':
        return <GoogleAds onHover={handleHover} />;
      case 'hubspot':
        return <HubSpot onHover={handleHover} />;
      case 'design-tracker':
        return <DesignTracker onHover={handleHover} />;
      case 'shopify-merch':
        return <ShopifyMerchandising onHover={handleHover} />;
      case 'category-ops':
        return <CategoryOps onHover={handleHover} />;
      case 'unicommerce':
        return <Unicommerce onHover={handleHover} />;
      case 'bitespeed':
        return <Bitespeed onHover={handleHover} />;
      case 'shopify-tags':
        return <ShopifyTags onHover={handleHover} />;
      default:
        return <Overview onHover={handleHover} />;
    }
  };

  if (loading) {
    return (
      <div data-testid="app-loading-screen" className="min-h-screen w-full flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }}>
        <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-widest">
          <span className="h-2 w-2 rounded-full bg-blue-600 animate-pulse" /> Loading Nooe Intelligence Studio...
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="h-screen w-screen bg-[#eef2f6] flex font-sans select-none antialiased text-slate-800 overflow-hidden" id="nooe-app">
      <Sidebar
        activePage={activePage}
        setActivePage={setActivePage}
        allowedModules={user.modules}
        userEmail={user.email}
        onLogout={logout}
      />

      <div ref={mainContentRef} className="flex-1 min-w-0 p-6 md:p-8 flex flex-col justify-between overflow-y-auto h-full">
        <main className="flex-1 min-w-0 space-y-6 flex flex-col justify-between">
          <div className="flex-1">
            <header className="flex justify-between items-center text-xs text-slate-500 font-mono pb-4 border-b border-slate-200/60 mb-6">
              <div className="flex items-center gap-2 bg-white/60 px-3 py-1.5 rounded-full border border-slate-200/40 shadow-xs font-semibold text-slate-600">
                <Calendar className="h-3.5 w-3.5 text-blue-600" />
                <span>Q2 REPORTING PERIOD</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="profile-menu-button"
                  onClick={() => setShowProfileMenu(true)}
                  title="View profile & change password"
                  className="flex items-center gap-2 bg-white/60 hover:bg-white active:scale-95 px-3 py-1.5 rounded-full border border-slate-200/40 hover:border-slate-300 shadow-xs font-semibold text-slate-600 transition-all cursor-pointer"
                >
                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span>{user.email.toUpperCase()} ({user.role})</span>
                </button>
                {user.role === 'Super Admin' && (
                  <button
                    data-testid="admin-console-button"
                    onClick={() => setShowAdminPortal(true)}
                    title="Open Admin Console"
                    className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-950 active:scale-95 text-white px-3 py-1.5 rounded-full shadow-xs font-semibold transition-all cursor-pointer"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    <span>Admin Console</span>
                  </button>
                )}
              </div>
            </header>

            {/* Active Platform View */}
            {drillDownId ? (
              <DrillDownDashboard id={drillDownId} onClose={() => setDrillDownId(null)} />
            ) : (
              renderActivePage()
            )}
          </div>

          {/* Footer Copyright */}
          <footer className="text-center text-[10px] text-slate-400 font-semibold mt-12 pt-4 border-t border-slate-200/30">
            Nooe Analytics Dashboard • Designed for High Performance Brand Decision-Making.
          </footer>
        </main>
      </div>

      {/* Chatbot Assist Widget */}
      <Chatbot />

      {/* Admin Control Facilities Modal */}
      {showAdminPortal && user.role === 'Super Admin' && (
        <AdminPortal onClose={() => setShowAdminPortal(false)} />
      )}

      {/* Profile & Change Password */}
      {showProfileMenu && (
        <ProfileMenu user={user} onClose={() => setShowProfileMenu(false)} />
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <GlobalDateRangeProvider>
        <DashboardShell />
      </GlobalDateRangeProvider>
    </AuthProvider>
  );
}
